import { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { geocode } from '../lib/api';

// Fix Leaflet's default marker icons broken by Vite's asset bundling.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const FALLBACK_CENTER: [number, number] = [30.3056, -81.7264]; // Murray Hill / Glen Laura area

interface Props {
  /** Free-form address used as the circle's center. Geocoded via /api/geocode. */
  address: string;
  /** Radius of the discovery circle, in meters. */
  radiusMeters: number;
}

/** Re-fits the map to the circle bounds when center or radius changes. */
function FitToCircle({ center, radiusMeters }: { center: [number, number]; radiusMeters: number }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !radiusMeters || radiusMeters <= 0) return;
    // `L.latLng(...).toBounds(sizeMeters)` returns a square bounding box whose
    // half-side equals the radius — exactly what we need to frame the circle.
    // (Avoids `L.circle().getBounds()`, which requires the circle to be
    // attached to a map for projection and otherwise throws
    // `layerPointToLatLng of undefined`.)
    const bounds = L.latLng(center[0], center[1]).toBounds(radiusMeters * 2);
    map.fitBounds(bounds, { padding: [24, 24] });
  }, [center, radiusMeters, map]);
  return null;
}

/**
 * Small read-only map preview that visualizes the discovery radius as a circle
 * centered on the configured home-base address. Used on the admin Settings
 * page so admins can see at a glance what neighborhoods a given radius covers.
 */
export default function DiscoveryRadiusMap({ address, radiusMeters }: Props) {
  const [center, setCenter] = useState<[number, number] | null>(null);
  const [resolvedLabel, setResolvedLabel] = useState<string>(address);
  const [status, setStatus] = useState<'loading' | 'ok' | 'fallback'>('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    void (async () => {
      const r = await geocode(address).catch(() => null);
      if (cancelled) return;
      if (r) {
        setCenter([r.lat, r.lng]);
        setResolvedLabel(r.formattedAddress);
        setStatus('ok');
      } else {
        setCenter(FALLBACK_CENTER);
        setResolvedLabel(`${address} (approximate — geocoding failed)`);
        setStatus('fallback');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [address]);

  if (!center) {
    return (
      <div
        className="border rounded bg-body-tertiary d-flex align-items-center justify-content-center mh-map-preview"
        role="status"
        aria-live="polite"
      >
        <span className="text-muted">
          <i className="bi bi-arrow-repeat me-2" aria-hidden="true" />
          Loading map…
        </span>
      </div>
    );
  }

  const miles = radiusMeters / 1609.344;
  // Leaflet writes the SVG `stroke` attribute directly, which does not resolve
  // CSS custom properties — so read the computed value of --mh-primary off
  // :root and pass a literal hex/rgb string. Falls back to the brand blue.
  const strokeColor =
    (typeof window !== 'undefined' &&
      getComputedStyle(document.documentElement).getPropertyValue('--mh-primary').trim()) ||
    '#2a5d84';

  return (
    <div>
      <div
        className="border rounded overflow-hidden mh-map-preview"
        aria-label={`Map showing a ${miles.toFixed(2)} mile discovery radius around ${resolvedLabel}`}
      >
        <MapContainer
          center={center}
          zoom={13}
          scrollWheelZoom={false}
          className="mh-map-fill"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={center}>
            <Popup>{resolvedLabel}</Popup>
          </Marker>
          {radiusMeters > 0 && (
            <Circle
              center={center}
              radius={radiusMeters}
              pathOptions={{ color: strokeColor, fillOpacity: 0.15 }}
            />
          )}
          <FitToCircle center={center} radiusMeters={radiusMeters} />
        </MapContainer>
      </div>
      <div className="form-text mt-1">
        {status === 'fallback' ? (
          <span className="text-warning">
            <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
            Could not geocode <code>{address}</code>; showing approximate location.
          </span>
        ) : (
          <>
            Radius of <strong>{miles.toFixed(2)} mi</strong> ({radiusMeters.toLocaleString()} m) centered on{' '}
            <span className="text-muted">{resolvedLabel}</span>.
          </>
        )}
      </div>
    </div>
  );
}
